import { Session } from '@ais-ui/auth';

import { ReportBackendTypes } from '../db-server/createReports';

type AccessType = 'Allow' | 'Deny';
type SubjectType = 'Type'; // Add other types if they exist

interface AceTblEntry {
  accessMask: number;
  accessType: AccessType;
  groupUuid: string;
  primitiveUuid: string;
  subjectType: SubjectType;
  subjectUuid: string;
}

interface AccessRightsResponse {
  aceTbl: AceTblEntry[];
  sessionUuid: string;
  userUuid: string;
}

// Specify the expected keys explicitly to gain the benefit of TypeScript's strictness
export const permissionMapping: Record<
  | 'GHT-RIBBON-SNAPIN-NEXTPORT'
  | 'GHT-RIBBON-SNAPIN-FILTER'
  | 'GHT-RIBBON-SNAPIN-SHAPES'
  | 'GHT-RIBBON-SNAPIN-SETTINGS'
  | 'GHT-RIBBON-SNAPIN-EVENT'
  | 'GHT-RIBBON-SNAPIN-COVERAGE'
  | 'GHT-RIBBON-SNAPIN-ADMINEVENT'
  | 'USE-WORLDSHAPE'
  | 'GHT-RIBBON-SNAPIN-REPORTLICENSE'
  | 'GHT-RIBBON-SNAPIN-REPLAY'
  | 'GHT-RIBBON-SNAPIN-ETA'
  | 'GHT-RIBBON-SNAPIN-MESSAGES'
  | 'GHT-RIBBON-SNAPIN-ATON'
  | 'GHT-RIBBON-SNAPIN-PILOT'
  | 'GHT-RIBBON-SNAPIN-PORT'
  | 'GHT-MDM-ADVANCEDVESSELINFO'
  | 'GHT-MDM-REGISTER'
  | 'GHT-MDM-INTEGRITY'
  | 'GHT-MDM-GROUNDING'
  | 'GHT-MDM-GPS-LOST'
  | 'PROFILE/AREA-STAT-TB'
  | 'PROFILE/ATON-DETAILED'
  | 'PROFILE/ATON-OVERVIEW'
  | 'PROFILE/CSV-DATA-EXPORT'
  | 'PROFILE/DATA-PASSAGELINE-MULTI'
  | 'PROFILE/COVERAGE'
  | 'PROFILE/DENSITY-TB'
  | 'PROFILE/FERRY-ROUTE'
  | 'PROFILE/FISHING'
  | 'PROFILE/HISTORY-TRACK-TB'
  | 'PROFILE/HISTORICAL-TRIPS'
  | 'PROFILE/MULTI-AREA'
  | 'PROFILE/NEARMISSSHIPTOSHIP-TB'
  | 'PROFILE/NMSP2-TB'
  | 'PROFILE/PASSAGELINE-TB'
  | 'PROFILE/PILOTBOAT-TRIPS'
  | 'PROFILE/PORT-CALLS'
  | 'PROFILE/REPLAY-VIDEO'
  | 'PROFILE/SOG-DISTRIBUTION'
  | 'PROFILE/GENERIC-SQL'
  | 'PROFILE/SHIPS-REPORT-TB'
  | 'PROFILE/TRANSIT-PILOTAGE'
  | 'PROFILE/TRANSPONDER-OFF'
  | 'PROFILE/TUGBOAT-OPERATIONS'
  | 'PROFILE/WDEVENT'
  | 'PROFILE/DATA-EXPORT-TB',
  string
> = {
  'GHT-RIBBON-SNAPIN-NEXTPORT': 'nextport',
  'GHT-RIBBON-SNAPIN-FILTER': 'filter',
  'GHT-RIBBON-SNAPIN-SHAPES': 'shapes',
  'GHT-RIBBON-SNAPIN-SETTINGS': 'settings',
  'GHT-RIBBON-SNAPIN-EVENT': 'events',
  'GHT-RIBBON-SNAPIN-COVERAGE': 'coverage',
  'GHT-RIBBON-SNAPIN-ADMINEVENT': 'eventdetector',
  'USE-WORLDSHAPE': 'useWorldShape',
  'GHT-RIBBON-SNAPIN-REPORTLICENSE': 'reports',
  'GHT-RIBBON-SNAPIN-REPLAY': 'replay',
  'GHT-RIBBON-SNAPIN-ETA': 'routes',
  'GHT-RIBBON-SNAPIN-MESSAGES': 'messages',
  'GHT-RIBBON-SNAPIN-ATON': 'atons',
  'GHT-RIBBON-SNAPIN-PILOT': 'pilot',
  'GHT-RIBBON-SNAPIN-PORT': 'port',
  'GHT-MDM-ADVANCEDVESSELINFO': 'mdmAdvancedVesselInfo',
  'GHT-MDM-REGISTER': 'mdmRegister',
  'GHT-MDM-INTEGRITY': 'mdmIntegrity',
  'GHT-MDM-GROUNDING': 'grounding',
  'GHT-MDM-GPS-LOST': 'gpsLost',
  'PROFILE/AREA-STAT-TB': ReportBackendTypes.AREA,
  'PROFILE/ATON-DETAILED': ReportBackendTypes.ATON_DETAILED,
  'PROFILE/ATON-OVERVIEW': ReportBackendTypes.ATON_OVERVIEW,
  'PROFILE/CSV-DATA-EXPORT': ReportBackendTypes.CSV_DATA_EXPORT,
  'PROFILE/DATA-PASSAGELINE-MULTI': ReportBackendTypes.PASSAGELINE_SHIPS,
  'PROFILE/COVERAGE': ReportBackendTypes.COVERAGE,
  'PROFILE/DENSITY-TB': ReportBackendTypes.DENSITY,
  'PROFILE/FERRY-ROUTE': ReportBackendTypes.FERRY_ROUTE,
  'PROFILE/FISHING': ReportBackendTypes.FISHING,
  'PROFILE/HISTORY-TRACK-TB': ReportBackendTypes.TRACK,
  'PROFILE/HISTORICAL-TRIPS': ReportBackendTypes.TRIPS,
  'PROFILE/MULTI-AREA': ReportBackendTypes.MULTI_SHAPE,
  'PROFILE/NEARMISSSHIPTOSHIP-TB': ReportBackendTypes.NEAR_MISS_SHIP_TO_SHIP,
  'PROFILE/NMSP2-TB': ReportBackendTypes.NEAR_MISS_SHIP_TO_POINT,
  'PROFILE/PASSAGELINE-TB': ReportBackendTypes.PASSAGELINE,
  'PROFILE/PILOTBOAT-TRIPS': ReportBackendTypes.PILOT_OPERATIONS,
  'PROFILE/PORT-CALLS': ReportBackendTypes.PORT_CALLS,
  'PROFILE/REPLAY-VIDEO': ReportBackendTypes.REPLAY_VIDEO,
  'PROFILE/SOG-DISTRIBUTION': ReportBackendTypes.SOG_DISTRIBUTION,
  'PROFILE/GENERIC-SQL': ReportBackendTypes.SQL,
  'PROFILE/SHIPS-REPORT-TB': ReportBackendTypes.SHIPS,
  'PROFILE/TRANSIT-PILOTAGE': ReportBackendTypes.TRANSIT_PILOTAGE,
  'PROFILE/TRANSPONDER-OFF': ReportBackendTypes.TRANSPONDER_OFF,
  'PROFILE/TUGBOAT-OPERATIONS': ReportBackendTypes.TUGBOAT_OPERATIONS,
  'PROFILE/WDEVENT': ReportBackendTypes.WATCHDOG_EVENTS,
  'PROFILE/DATA-EXPORT-TB': ReportBackendTypes.CSV_DATA_EXPORT_FAST,
};

export type MappedPermissions = typeof permissionMapping;
export type MakePermissions<
  T extends Record<string | number | symbol, string>
> = {
  [K in T[keyof T]]: boolean;
};

interface UniqueEventdetectorPair {
  primitiveUuid: string;
  subjectUuid: string;
}

function findAndCheckAccess(
  data: AceTblEntry[],
  pairs: UniqueEventdetectorPair[]
) {
  return pairs.filter((pair) =>
    data.some(
      (item) =>
        item.primitiveUuid === pair.primitiveUuid &&
        item.subjectUuid === pair.subjectUuid &&
        item.accessType.toLowerCase().includes('allow')
    )
  );
}

const setReportPermissions = (
  permissions: MakePermissions<MappedPermissions>,
  data: AceTblEntry[]
) => {
  const filteredReports = data.filter(
    (report) =>
      report.subjectUuid === 'GHT-RIBBON-SNAPIN-REPORT' &&
      report.accessType.toLowerCase().includes('allow')
  );

  const allowedReportTypes = new Set(
    filteredReports.map((report) => report.primitiveUuid.replace(/-/g, '_'))
  );

  // The ATON OVERVIEW permission doesn't match the report type name so we have to add it explicitly
  if (allowedReportTypes.has('PROFILE/ATON_OVERVIEW')) {
    allowedReportTypes.add('PROFILE/ATON_OVERVIEW_REPORT');
  }

  // The ATON DETAILED permission doesn't match the report type name so we have to add it explicitly
  if (allowedReportTypes.has('PROFILE/ATON_DETAILED')) {
    allowedReportTypes.add('PROFILE/ATON_DETAILED_REPORT');
  }

  // Step 2: Update permissions object
  Object.values(ReportBackendTypes).forEach((reportType) => {
    // Transforming reportType to match the format in the permission reports
    const permissionReportType = `PROFILE/${reportType}`;

    permissions[reportType] = allowedReportTypes.has(permissionReportType);
  });
};

const setEventDetectorPermissions = (
  permissions: MakePermissions<MappedPermissions>,
  data: AceTblEntry[]
) => {
  permissions['eventdetectorArea'] = false;
  permissions['eventdetectorLine'] = false;
  permissions['eventdetectorEta'] = false;
  permissions['eventdetectorCable'] = false;
  permissions['eventdetectorAtbAtd'] = false;
  permissions['eventdetectorFishing'] = false;

  const watchdogToPermissionMap = {
    'REGION-WATCHDOG': 'eventdetectorArea',
    'PASSAGELINE-WATCHDOG': 'eventdetectorLine',
    'ETA-WATCHDOG': 'eventdetectorEta',
    'CABLE-WATCHDOG': 'eventdetectorCable',
    'ATB_ATD-WATCHDOG': 'eventdetectorAtbAtd',
    'FISH-WATCHDOG': 'eventdetectorFishing',
  };

  // Eventdetector permissions
  const uniqueEventDetectorPairs = [
    {
      primitiveUuid: 'REGION-WATCHDOG',
      subjectUuid: 'GHT-RIBBON-SNAPIN-ADMINEVENT',
    },
    {
      primitiveUuid: 'PASSAGELINE-WATCHDOG',
      subjectUuid: 'GHT-RIBBON-SNAPIN-ADMINEVENT',
    },
    {
      primitiveUuid: 'ETA-WATCHDOG',
      subjectUuid: 'GHT-RIBBON-SNAPIN-ADMINEVENT',
    },
    {
      primitiveUuid: 'CABLE-WATCHDOG',
      subjectUuid: 'GHT-RIBBON-SNAPIN-ADMINEVENT',
    },
    {
      primitiveUuid: 'ATB_ATD-WATCHDOG',
      subjectUuid: 'GHT-RIBBON-SNAPIN-ADMINEVENT',
    },
    {
      primitiveUuid: 'FISH-WATCHDOG',
      subjectUuid: 'GHT-RIBBON-SNAPIN-ADMINEVENT',
    },
  ];

  const foundPairs = findAndCheckAccess(data, uniqueEventDetectorPairs);

  foundPairs.forEach((foundPair: UniqueEventdetectorPair) => {
    if (foundPair.primitiveUuid in watchdogToPermissionMap) {
      const permissionKey =
        watchdogToPermissionMap[
          foundPair.primitiveUuid as keyof typeof watchdogToPermissionMap
        ];
      permissions[permissionKey] = true;
    }
  });
};

const setPackagePermissions = (
  permissions: MakePermissions<MappedPermissions>,
  data: AceTblEntry[]
) => {
  // Outer layer permissions such as reports, events and settings
  for (const permissionKey in permissionMapping) {
    const permissionValue =
      permissionMapping[permissionKey as keyof typeof permissionMapping];

    const existsInAceTbl = data.some(
      (entry) =>
        (entry.subjectUuid === permissionKey ||
          entry.primitiveUuid === permissionKey) &&
        entry.accessType.toLowerCase().includes('allow')
    );
    permissions[permissionValue as keyof typeof permissions] = existsInAceTbl;
  }
};

export const getPermissions = async (
  session: Session
): Promise<MakePermissions<MappedPermissions>> => {
  const response: AccessRightsResponse = await session.request(
    'getAccessToken',
    {}
  );

  // Dynamically initialize permissions based on permissionMapping
  const permissions: MakePermissions<MappedPermissions> = {};

  setPackagePermissions(permissions, response.aceTbl);
  setEventDetectorPermissions(permissions, response.aceTbl);
  setReportPermissions(permissions, response.aceTbl);

  return permissions;
};
